import { X, Loader, CheckCircle, AlertTriangle, ExternalLink } from "lucide-react";
import { useState, useEffect } from "react";

export const inputCls =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";

export const Badge = ({ color, children }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}
  >
    {children}
  </span>
);

export const Btn = ({
  onClick,
  disabled,
  loading,
  variant = "primary",
  size = "md",
  children,
  className = "",
  type = "button",
}) => {
  const variants = {
    primary: "bg-primary text-white hover:bg-primary-dark",
    secondary: "bg-gray-100 text-gray-700 hover:bg-gray-200",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "text-gray-600 hover:bg-gray-100",
  };
  const sizes = {
    sm: "px-2.5 py-1.5 text-xs",
    md: "px-3.5 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {loading && <Loader size={14} className="animate-spin" />}
      {children}
    </button>
  );
};

export const Modal = ({ title, onClose, children, size = "md", footer }) => {
  const widths = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-xl shadow-2xl w-full ${widths[size]} max-h-[90vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export const ConfirmModal = ({
  open,
  title = "Confirmer",
  message,
  onConfirm,
  onCancel,
  danger = false,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="px-5 py-4">
          <h2 className="text-base font-semibold text-gray-800 mb-2">
            {title}
          </h2>
          <p className="text-sm text-gray-600">{message}</p>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
          <Btn variant="secondary" onClick={onCancel}>
            Annuler
          </Btn>
          <Btn variant={danger ? "danger" : "primary"} onClick={onConfirm}>
            Confirmer
          </Btn>
        </div>
      </div>
    </div>
  );
};

export const FormField = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

export const Toast = ({ message, show, onHide, type = "success" }) => {
  useEffect(() => {
    if (show) {
      const t = setTimeout(onHide, 3000);
      return () => clearTimeout(t);
    }
  }, [show, onHide]);

  if (!show) return null;

  const styles = {
    success: "bg-green-600 text-white",
    error: "bg-red-600 text-white",
  };
  const Icon = type === "success" ? CheckCircle : AlertTriangle;

  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${styles[type]}`}
    >
      <Icon size={16} />
      {message}
    </div>
  );
};

export const fmtCurrency = (v) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v);

export const fmtDate = (d) =>
  !d ? "—" : new Date(d).toLocaleDateString("fr-FR");

function mimeFromFileName(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return "application/pdf";
  if (["jpg", "jpeg"].includes(ext)) return "image/jpeg";
  if (ext === "png") return "image/png";
  return "application/octet-stream";
}

export function DocViewer({ fileName, mimeType, fetchBlob, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let url;
    fetchBlob()
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        setBlobUrl(url);
      })
      .catch(() => setError(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, []);

  const mime = mimeType || mimeFromFileName(fileName);
  const isPdf = mime === "application/pdf";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-6">
      <div className="bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-4xl" style={{ height: "80vh" }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <span className="text-sm font-medium text-gray-700 truncate max-w-xs">{fileName}</span>
          <div className="flex items-center gap-2">
            {blobUrl && (
              <button
                onClick={() => window.open(blobUrl, "_blank")}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary border border-primary rounded-lg hover:bg-primary/5"
              >
                <ExternalLink size={13} /> Nouvel onglet
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          {error ? (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              Impossible de charger le fichier.
            </div>
          ) : !blobUrl ? (
            <div className="flex items-center justify-center h-full">
              <Loader className="animate-spin text-primary" size={28} />
            </div>
          ) : isPdf ? (
            <iframe
              src={blobUrl}
              title={fileName}
              className="w-full h-full border-0"
            />
          ) : (
            <div className="flex items-center justify-center h-full p-4 bg-gray-50">
              <img
                src={blobUrl}
                alt={fileName}
                className="max-w-full max-h-full object-contain rounded-lg shadow"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
